import { Article } from '../types';

export const inventoryManagementArticle: Article = {
  id: 'inventory-management-guide',
  title: 'Inventory Management',
  slug: 'inventory-management',
  description: 'Learn how to add, edit, and track inventory items including costs and quantities',
  category: 'orders',
  tags: ['inventory', 'stock', 'products', 'tracking', 'items', 'costs'],
  lastUpdated: new Date('2024-02-06'),
  readTime: 8,
  content: `
    <h2>Overview of Inventory Management</h2>
    <p>The inventory management system allows you to track your products and supplies with their costs and quantities. This helps you maintain accurate records of what you have in stock and the total value of your inventory.</p>

    <div class="bg-blue-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Key Features:</strong>
      <ul>
        <li>Add and manage inventory items</li>
        <li>Track item costs and quantities</li>
        <li>Calculate total inventory value</li>
        <li>Search and filter items</li>
        <li>Edit and update item details</li>
        <li>Visual indicators for stock levels</li>
      </ul>
    </div>

    <h2 id="accessing-inventory">Accessing Inventory Management</h2>
    
    <p>To access the inventory system:</p>
    <ol>
      <li>Log in to your CRM at <code>https://app.thedefinedcrm.com</code></li>
      <li>Click on <strong>Inventory</strong> in the left sidebar menu</li>
      <li>You'll see the inventory management page with:
        <ul>
          <li>Add/Edit item form on the left</li>
          <li>Inventory statistics at the top right</li>
          <li>Search bar for filtering items</li>
          <li>Table of all inventory items below</li>
        </ul>
      </li>
    </ol>

    <h2 id="adding-items">Adding New Inventory Items</h2>
    
    <h3>Step 1: Open the Add Item Form</h3>
    <p>The form is located on the left side of the inventory page. If you're editing an existing item, click "Clear" to start fresh.</p>

    <h3>Step 2: Fill in Item Details</h3>
    <p>Enter the following required information:</p>
    <ul>
      <li><strong>Item Name:</strong> 
        <ul>
          <li>Enter a descriptive name for the product</li>
          <li>Example: "Glass Cleaner 32oz", "Microfiber Cloths Pack"</li>
        </ul>
      </li>
      <li><strong>Item ID:</strong>
        <ul>
          <li>Create a unique identifier for the item</li>
          <li>This could be a SKU, product code, or your own numbering system</li>
          <li>Example: "GC-32", "MFC-10PK"</li>
          <li>Note: Item ID cannot be changed after creation</li>
        </ul>
      </li>
      <li><strong>Description:</strong>
        <ul>
          <li>Provide details about the item</li>
          <li>Include brand, specifications, or usage information</li>
          <li>Example: "Professional strength ammonia-free glass cleaner, safe for tinted windows"</li>
        </ul>
      </li>
      <li><strong>Cost ($):</strong>
        <ul>
          <li>Enter the cost per unit</li>
          <li>This is what you pay for the item</li>
          <li>Must be greater than 0</li>
          <li>Example: "12.50"</li>
        </ul>
      </li>
      <li><strong>Quantity:</strong>
        <ul>
          <li>Enter the current stock quantity</li>
          <li>Must be 0 or greater</li>
          <li>Example: "25"</li>
        </ul>
      </li>
    </ul>

    <h3>Step 3: Save the Item</h3>
    <ol>
      <li>Review all entered information for accuracy</li>
      <li>Click the <strong>Save Item</strong> button</li>
      <li>You'll see a success message when the item is added</li>
      <li>The form will clear automatically</li>
      <li>The new item will appear in the inventory table</li>
    </ol>

    <div class="bg-yellow-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Important:</strong> All fields are required. The system will show an error message if any field is missing or invalid.
    </div>

    <h2 id="viewing-inventory">Viewing Your Inventory</h2>
    
    <h3>Inventory Statistics</h3>
    <p>At the top right of the page, you'll see two summary cards:</p>
    <ul>
      <li><strong>Total Value:</strong> The combined value of all items (cost × quantity for each item)</li>
      <li><strong>Total Items:</strong> The total quantity of all items in stock</li>
    </ul>

    <h3>Inventory Table</h3>
    <p>The main table displays all your inventory items with the following columns:</p>
    <ul>
      <li><strong>Name:</strong> The item's name</li>
      <li><strong>ID:</strong> The unique item identifier (shown in a chip/badge)</li>
      <li><strong>Description:</strong> Item details (hover to see full description)</li>
      <li><strong>Cost:</strong> Price per unit</li>
      <li><strong>Quantity:</strong> Current stock level with color coding:
        <ul>
          <li>🟢 Green: More than 20 items (good stock)</li>
          <li>🟡 Yellow: Between 6-20 items (moderate stock)</li>
          <li>🔴 Red: 5 or fewer items (low stock)</li>
        </ul>
      </li>
      <li><strong>Value:</strong> Total value for that item (cost × quantity)</li>
      <li><strong>Actions:</strong> Edit and delete buttons</li>
    </ul>

    <h2 id="searching-items">Searching for Items</h2>
    
    <p>Use the search bar above the inventory table to quickly find items:</p>
    <ol>
      <li>Click in the search field</li>
      <li>Type any part of:
        <ul>
          <li>Item name</li>
          <li>Item ID</li>
          <li>Description</li>
        </ul>
      </li>
      <li>The table will automatically filter as you type</li>
      <li>Clear the search to see all items again</li>
    </ol>

    <h3>Search Examples</h3>
    <ul>
      <li>Search "glass" to find all glass cleaning products</li>
      <li>Search "32oz" to find all 32-ounce items</li>
      <li>Search by ID like "MFC" to find specific product codes</li>
    </ul>

    <h2 id="editing-items">Editing Existing Items</h2>
    
    <h3>Step 1: Select Item to Edit</h3>
    <ol>
      <li>Find the item in the inventory table</li>
      <li>Click the <strong>Edit</strong> button (pencil icon) in the Actions column</li>
      <li>The form will populate with the item's current information</li>
      <li>The page will scroll to the top automatically</li>
    </ol>

    <h3>Step 2: Make Changes</h3>
    <p>You can modify the following fields:</p>
    <ul>
      <li>✓ Item Name</li>
      <li>✗ Item ID (disabled - cannot be changed)</li>
      <li>✓ Description</li>
      <li>✓ Cost</li>
      <li>✓ Quantity</li>
    </ul>

    <h3>Step 3: Save Updates</h3>
    <ol>
      <li>After making changes, click <strong>Update Item</strong></li>
      <li>The item will be updated in the table</li>
      <li>You'll see a success message</li>
      <li>The form will clear</li>
    </ol>

    <div class="bg-blue-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Tip:</strong> Use the edit feature to update quantities as you use or receive inventory, keeping your stock levels accurate.
    </div>

    <h2 id="deleting-items">Deleting Items</h2>
    
    <p>To remove an item from inventory:</p>
    <ol>
      <li>Find the item in the table</li>
      <li>Click the <strong>Delete</strong> button (trash icon) in the Actions column</li>
      <li>A confirmation dialog will appear</li>
      <li>Click "OK" to confirm deletion</li>
      <li>The item will be permanently removed</li>
      <li>Statistics will update automatically</li>
    </ol>

    <div class="bg-red-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Warning:</strong> Deleting an item is permanent and cannot be undone. Make sure you want to remove the item completely before confirming.
    </div>

    <h2 id="stock-management">Managing Stock Levels</h2>
    
    <h3>Updating Quantities</h3>
    <p>To adjust stock levels:</p>
    <ol>
      <li>Click Edit on the item</li>
      <li>Update the Quantity field</li>
      <li>Click Update Item</li>
    </ol>

    <h3>Stock Level Indicators</h3>
    <p>The system uses color coding to help you quickly identify stock status:</p>
    <table>
      <thead>
        <tr>
          <th>Color</th>
          <th>Quantity Range</th>
          <th>Status</th>
          <th>Action Needed</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>🟢 Green</td>
          <td>More than 20</td>
          <td>Good Stock</td>
          <td>None</td>
        </tr>
        <tr>
          <td>🟡 Yellow</td>
          <td>6 to 20</td>
          <td>Moderate Stock</td>
          <td>Monitor, consider reordering</td>
        </tr>
        <tr>
          <td>🔴 Red</td>
          <td>5 or less</td>
          <td>Low Stock</td>
          <td>Reorder soon</td>
        </tr>
      </tbody>
    </table>

    <h3>Tracking Usage</h3>
    <p>While the system doesn't automatically track usage, you should:</p>
    <ul>
      <li>Update quantities when items are used</li>
      <li>Update quantities when new stock arrives</li>
      <li>Perform regular counts to ensure accuracy</li>
      <li>Use the description field to note reorder information</li>
    </ul>

    <h2 id="inventory-value">Understanding Inventory Value</h2>
    
    <h3>Individual Item Value</h3>
    <p>Each item's value is calculated as:</p>
    <ul>
      <li><strong>Formula:</strong> Cost × Quantity = Value</li>
      <li><strong>Example:</strong> $12.50 × 25 units = $312.50</li>
    </ul>

    <h3>Total Inventory Value</h3>
    <p>The total value shown at the top represents:</p>
    <ul>
      <li>Sum of all individual item values</li>
      <li>Updates automatically when you add, edit, or delete items</li>
      <li>Helps you understand how much capital is tied up in inventory</li>
    </ul>

    <h2 id="best-practices">Best Practices</h2>
    
    <h3>Item Naming</h3>
    <ul>
      <li>Use consistent naming conventions</li>
      <li>Include size or quantity in the name</li>
      <li>Add brand names if carrying multiple brands</li>
      <li>Examples:
        <ul>
          <li>"Windex Glass Cleaner 32oz"</li>
          <li>"3M Microfiber Cloths 10-Pack"</li>
          <li>"Clorox Disinfectant Wipes 75ct"</li>
        </ul>
      </li>
    </ul>

    <h3>Item IDs</h3>
    <ul>
      <li>Create a logical system for IDs</li>
      <li>Use prefixes for categories (GC for glass cleaner)</li>
      <li>Include size or type in the ID</li>
      <li>Keep IDs short but descriptive</li>
      <li>Document your ID system for team consistency</li>
    </ul>

    <h3>Regular Maintenance</h3>
    <ul>
      <li>✓ Update quantities weekly or after each job</li>
      <li>✓ Review low stock items regularly</li>
      <li>✓ Remove discontinued items</li>
      <li>✓ Verify costs are current</li>
      <li>✓ Perform physical counts monthly</li>
    </ul>

    <h3>Using Descriptions Effectively</h3>
    <ul>
      <li>Include supplier information</li>
      <li>Note reorder quantities</li>
      <li>Add usage instructions</li>
      <li>Specify storage requirements</li>
      <li>Example: "Supplier: ABC Supply, Reorder at 10 units, Store in cool dry place"</li>
    </ul>

    <h2 id="common-tasks">Common Tasks</h2>
    
    <h3>Receiving New Stock</h3>
    <ol>
      <li>When shipment arrives, locate items in inventory</li>
      <li>Click Edit for each item</li>
      <li>Add received quantity to current quantity</li>
      <li>Update cost if price changed</li>
      <li>Save changes</li>
    </ol>

    <h3>Using Items on Jobs</h3>
    <ol>
      <li>After completing a job, note items used</li>
      <li>Go to inventory page</li>
      <li>Edit each used item</li>
      <li>Subtract used quantity from current stock</li>
      <li>Update the item</li>
    </ol>

    <h3>Monthly Inventory Count</h3>
    <ol>
      <li>Print or export inventory list</li>
      <li>Physically count all items</li>
      <li>Compare to system quantities</li>
      <li>Update any discrepancies</li>
      <li>Investigate large differences</li>
    </ol>

    <h2 id="troubleshooting">Troubleshooting</h2>
    
    <h3>Common Issues</h3>
    <table>
      <thead>
        <tr>
          <th>Issue</th>
          <th>Cause</th>
          <th>Solution</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Cannot save item</td>
          <td>Missing required field</td>
          <td>Check all fields are filled, cost > 0</td>
        </tr>
        <tr>
          <td>Item ID already exists</td>
          <td>Duplicate ID</td>
          <td>Use a different unique ID</td>
        </tr>
        <tr>
          <td>Cannot edit Item ID</td>
          <td>IDs are permanent</td>
          <td>Delete and recreate if ID must change</td>
        </tr>
        <tr>
          <td>Search not working</td>
          <td>Special characters</td>
          <td>Try simpler search terms</td>
        </tr>
        <tr>
          <td>Wrong total value</td>
          <td>Browser cache</td>
          <td>Refresh the page</td>
        </tr>
      </tbody>
    </table>

    <h3>Data Validation Errors</h3>
    <ul>
      <li><strong>"Name is required"</strong> - Enter an item name</li>
      <li><strong>"Item ID is required"</strong> - Enter a unique identifier</li>
      <li><strong>"Cost must be greater than 0"</strong> - Enter a positive number</li>
      <li><strong>"Description is required"</strong> - Add item details</li>
      <li><strong>"Quantity must be 0 or greater"</strong> - Cannot be negative</li>
    </ul>

    <div class="bg-green-50 p-4 rounded-lg mt-6">
      <strong>Pro Tip:</strong> Export or screenshot your inventory list regularly as a backup. This helps if you need to reference historical data or recover from accidental deletions.
    </div>
  `,
  tableOfContents: [
    { id: 'accessing-inventory', title: 'Accessing Inventory' },
    { id: 'adding-items', title: 'Adding New Items' },
    { id: 'viewing-inventory', title: 'Viewing Your Inventory' },
    { id: 'searching-items', title: 'Searching for Items' },
    { id: 'editing-items', title: 'Editing Items' },
    { id: 'deleting-items', title: 'Deleting Items' },
    { id: 'stock-management', title: 'Managing Stock Levels' },
    { id: 'inventory-value', title: 'Understanding Value' },
    { id: 'best-practices', title: 'Best Practices' },
    { id: 'common-tasks', title: 'Common Tasks' },
    { id: 'troubleshooting', title: 'Troubleshooting' }
  ],
  relatedArticles: [
    'creating-orders',
    'service-management-guide'
  ]
};